/**
 * Postprocess — fullscreen quad that reads the scene FBO and writes to the
 * default framebuffer.
 *
 * Sprint 1: no-op copy. The interface is shaped so Sprint 3 can drop in a
 * velocity-blur or temporal-accumulation pass without touching the rest of
 * the engine.
 */

import { linkProgram, POST_ATTRIBS } from "./glContext";

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uScene;     // scene color
uniform sampler2D uVelocity;  // velocity (clip-space, normalized)
uniform float uBlurStrength;  // 0 disables blur; 1 = full frame motion
uniform int uVelocityFormat;  // 0=disabled, 1=RG8 (unscale /127), 2=RG16F
uniform vec2 uAspect;         // 1/aspect-corrected blur direction (use 1.0 since velocity is in clip space)
uniform float uTexelW;        // 1/sceneWidth
uniform float uTexelH;        // 1/sceneHeight

void main() {
  vec4 c = texture(uScene, vUV);

  // Skip blur taps when disabled or no velocity at this pixel.
  vec2 v;
  if (uVelocityFormat == 1) {
    v = texture(uVelocity, vUV).xy / 127.0;
  } else if (uVelocityFormat == 2) {
    v = texture(uVelocity, vUV).xy;
  } else {
    v = vec2(0.0);
  }

  float speed = length(v);
  // Threshold lowered from 0.001 to 0.0001 so very slow motion still blurs —
  // without this the slider feels dead on static scenes.
  if (uBlurStrength <= 0.0 || speed < 0.0001) {
    outColor = c;
    return;
  }

  // Clamp velocity magnitude so fast motion doesn't blow out into smear.
  // Conservative: 0.5 clip-space units = half the screen per frame. That's
  // pathological drag — cap to 0.25.
  const float MAX_VEL = 0.25;
  vec2 velDir = v / max(speed, 0.0001);
  float len = min(speed * uBlurStrength, MAX_VEL);

  // Speed-based fade: very slow motion shouldn't blur (would look jittery).
  // Tightened range — fade in from 0.0 to 0.005 (was 0.02) so even slow
  // ambient motion (orb float, EQ pulse) produces visible blur.
  float fade = smoothstep(0.0, 0.005, speed);
  len *= fade;

  // N-tap directional blur. Symmetric around the current sample.
  const int TAPS = 7;
  vec3 acc = c.rgb;
  float w = 1.0;
  for (int i = 1; i < TAPS; i++) {
    float t = (float(i) / float(TAPS - 1)) - 0.5;
    vec2 ofs = velDir * (t * len);
    vec4 s = texture(uScene, vUV + ofs);
    acc += s.rgb;
    w += 1.0;
  }
  outColor = vec4(acc / w, c.a);
}
`;

export interface PostProcessPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  quadBuffer: WebGLBuffer;
  uScene: WebGLUniformLocation | null;
  uVelocity: WebGLUniformLocation | null;
  uBlurStrength: WebGLUniformLocation | null;
  uVelocityFormat: WebGLUniformLocation | null;
  uAspect: WebGLUniformLocation | null;
  uTexelW: WebGLUniformLocation | null;
  uTexelH: WebGLUniformLocation | null;
}

const QUAD_VERTS = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

export function createPostProcessPipeline(gl: WebGL2RenderingContext): PostProcessPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const quadBuffer = gl.createBuffer();
  if (!vao || !quadBuffer) return null;

  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    quadBuffer,
    uScene: gl.getUniformLocation(program, "uScene"),
    uVelocity: gl.getUniformLocation(program, "uVelocity"),
    uBlurStrength: gl.getUniformLocation(program, "uBlurStrength"),
    uVelocityFormat: gl.getUniformLocation(program, "uVelocityFormat"),
    uAspect: gl.getUniformLocation(program, "uAspect"),
    uTexelW: gl.getUniformLocation(program, "uTexelW"),
    uTexelH: gl.getUniformLocation(program, "uTexelH"),
  };
}

export function drawPostProcess(
  gl: WebGL2RenderingContext,
  pipe: PostProcessPipeline,
  sceneTex: WebGLTexture,
  velocityTex: WebGLTexture | null,
  blurStrength: number,
  velocityFormat: 0 | 1 | 2,
  sceneW: number,
  sceneH: number,
): void {
  gl.useProgram(pipe.program);
  gl.bindVertexArray(pipe.vao);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, sceneTex);
  gl.uniform1i(pipe.uScene, 0);

  if (velocityTex) {
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, velocityTex);
    gl.uniform1i(pipe.uVelocity, 1);
  } else {
    // Bind something to satisfy the sampler even when disabled.
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, sceneTex);
    gl.uniform1i(pipe.uVelocity, 1);
  }

  gl.uniform1f(pipe.uBlurStrength, blurStrength);
  gl.uniform1i(pipe.uVelocityFormat, velocityFormat);
  gl.uniform2f(pipe.uAspect, 1, 1); // velocity is already in clip space
  gl.uniform1f(pipe.uTexelW, 1 / Math.max(1, sceneW));
  gl.uniform1f(pipe.uTexelH, 1 / Math.max(1, sceneH));

  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
